import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AuthUser,
  getSessionUser,
  logout as requestLogout,
} from "shared/auth/auth";
import { sendGTMEvent } from "shared/services/GTM";
import { getPlatformType } from "shared/utils/platform";

type AuthStatus = "loading" | "authenticated" | "anonymous";

type AuthContextValue = {
  status: AuthStatus;
  isAuthenticated: boolean;
  user: AuthUser | null;
  completeAuthentication: (user?: AuthUser | null) => void;
  refreshSession: () => Promise<AuthUser | null>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const sessionRestoredEventSent = useRef(false);

  const completeAuthentication = useCallback(
    (authenticatedUser: AuthUser | null = null) => {
      setUser(authenticatedUser);
      setStatus("authenticated");
    },
    [],
  );

  const logout = useCallback(async () => {
    await requestLogout();
    setUser(null);
    setStatus("anonymous");
  }, []);

  const refreshSession = useCallback(async () => {
    const sessionUser = await getSessionUser();
    setUser(sessionUser);
    setStatus(sessionUser ? "authenticated" : "anonymous");
    return sessionUser;
  }, []);

  useEffect(() => {
    let active = true;

    refreshSession()
      .then((sessionUser) => {
        if (!active) return;

        if (
          sessionUser &&
          window.location.pathname !== "/auth/callback" &&
          !sessionRestoredEventSent.current
        ) {
          sessionRestoredEventSent.current = true;
          sendGTMEvent("authSessionRestored", {
            platform: getPlatformType(),
            pagePath: window.location.pathname,
          });
        }
      })
      .catch(() => {
        if (active) setStatus("anonymous");
      });

    return () => {
      active = false;
    };
  }, [refreshSession]);

  const value = useMemo(
    () => ({
      status,
      isAuthenticated: status === "authenticated",
      user,
      completeAuthentication,
      refreshSession,
      logout,
    }),
    [completeAuthentication, logout, refreshSession, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
